/**
 * Top GDELT media headlines behind a term's coverage (S1a ArtList forward
 * capture) — the article-level provenance that makes a media spike self-
 * explaining. Newest-first, domain + date + outbound link.
 *
 * Empty until a GDELT collect run with article capture has populated
 * salienceArticles for this term (capture is forward-only; ArtList covers ~last
 * 3 months — plan §6.2).
 */
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { apiSalience } from "../../lib/api";
import { shortDate } from "./format";

export function CoverageHeadlines({ termId }: { termId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["salience", "articles", termId],
    queryFn: () => apiSalience.articles({ term: termId }),
    enabled: !!termId,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-white/5" />
        ))}
      </div>
    );
  }

  const articles = data ?? [];
  if (articles.length === 0) {
    return (
      <div className="text-sm text-terminal-ink-muted">
        No captured headlines for this term yet — the spike pass captures the
        top articles behind each media spike; expand a spike in the feed to see
        them in context.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-white/8">
      {articles.slice(0, 14).map((a) => (
        <li key={a.url} className="py-2 first:pt-0">
          <a
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-2"
          >
            <span className="mt-0.5 shrink-0 font-mono text-[11px] tabular-nums text-terminal-ink-muted">
              {shortDate(a.seenDate)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-terminal-ink group-hover:underline">
                {a.title || a.url}
              </span>
              <span className="text-[11px] text-terminal-ink-muted">
                {a.domain}
                {a.sourceCountry ? ` · ${a.sourceCountry}` : ""}
              </span>
            </span>
            <ExternalLink
              size={13}
              className="mt-0.5 shrink-0 text-terminal-ink-muted opacity-0 transition-opacity group-hover:opacity-100"
            />
          </a>
        </li>
      ))}
    </ul>
  );
}
